import type { ComponentDef, ComponentCategory } from './component-def.js';
import { makeInitialPinState } from '../model/pin.js';
import type { ComponentInstance } from '../model/component.js';

/**
 * Carga y catálogo de definiciones de componentes.
 * En el MVP, las definiciones vienen como JSONs estáticos servidos por el host
 * (por defecto en /components/…). El loader las cachea en memoria.
 */

/** Entrada del índice de biblioteca (public/components/index.json). */
export interface LibraryIndexEntry {
  id: string;
  name: string;
  category: ComponentCategory;
  /** Ruta relativa al baseUrl del JSON completo de la definición. */
  path: string;
  /** Marca de componente "genérico" (sin PN/valor específico). Se ordenan primero. */
  generic?: boolean;
}

export interface LibraryIndex {
  components: LibraryIndexEntry[];
}

export class ComponentLibrary {
  private readonly baseUrl: string;
  private readonly cache = new Map<string, ComponentDef>();
  private index: LibraryIndex | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  }

  /** Carga y cachea el índice de biblioteca. */
  async loadIndex(): Promise<LibraryIndex> {
    if (this.index) return this.index;
    const url = this.resolve('index.json');
    const res = await fetch(url);
    if (!res.ok) throw new Error(`No se pudo cargar el índice: ${url} (${res.status})`);
    this.index = (await res.json()) as LibraryIndex;
    return this.index;
  }

  /** Carga la definición asociada a una entrada del índice. */
  async loadFromIndex(entry: LibraryIndexEntry): Promise<ComponentDef> {
    return this.load(entry.path);
  }

  /**
   * Carga una definición por ruta relativa dentro de la biblioteca.
   * P. ej. "passive/resistor-quarter-watt.json".
   */
  async load(relativePath: string): Promise<ComponentDef> {
    const cached = this.cache.get(relativePath);
    if (cached) return cached;
    const url = this.resolve(relativePath);
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`No se pudo cargar la definición: ${url} (${res.status})`);
    }
    const def = (await res.json()) as ComponentDef;
    this.cache.set(relativePath, def);
    this.cache.set(def.id, def);
    return def;
  }

  private resolve(relativePath: string): string {
    const clean = relativePath.replace(/^\/+/, '');
    // Si el baseUrl es absoluto (http(s)://) usamos URL; si no, concatenamos.
    if (/^https?:\/\//.test(this.baseUrl)) {
      return new URL(clean, this.baseUrl).toString();
    }
    return `${this.baseUrl}${clean}`;
  }

  /** Devuelve una definición ya cargada por su id. */
  get(defId: string): ComponentDef | undefined {
    return this.cache.get(defId);
  }

  /**
   * Registra una definición en la cache sin ir a la red. Se usa al
   * abrir un fichero de proyecto con defs embebidas: la def del
   * fichero prevalece sobre cualquier versión previamente cargada
   * (garantiza que el proyecto se abre EXACTAMENTE como se guardó
   * aunque la biblioteca online haya evolucionado).
   */
  injectDef(def: ComponentDef): void {
    this.cache.set(def.id, def);
  }
}

/**
 * Utilidad para crear una instancia colocada en el proyecto a partir de
 * una definición. Genera un uid único simple.
 */
export function makeComponentInstance(
  def: ComponentDef,
  overrides: Partial<Pick<ComponentInstance, 'position_mm' | 'rotation_deg' | 'z_order' | 'value' | 'label'>> = {},
): ComponentInstance {
  return {
    uid: makeUid(),
    def_id: def.id,
    position_mm: overrides.position_mm ?? { x: 0, y: 0 },
    rotation_deg: overrides.rotation_deg ?? 0,
    z_order: overrides.z_order ?? 0,
    ...(overrides.value !== undefined ? { value: overrides.value } : {}),
    ...(overrides.label !== undefined ? { label: overrides.label } : {}),
    pins: def.pins.map((p) => makeInitialPinState(p.id, p.bend_points_count, p.initial_bends)),
  };
}

let uidCounter = 0;
function makeUid(): string {
  uidCounter += 1;
  return `u${Date.now().toString(36)}${uidCounter.toString(36)}`;
}
